import { useCallback, useMemo } from "react";

import { recordErrorInSentry } from "../../utils/init-sentry.js";
import { useDevicesStore } from "./audio-devices.store.js";
import {
  classifyAudioIssueKind,
  isRecoverableAudioError,
  reportTableCallAudioError,
} from "./table-call-audio-reporting.js";
import type { TableCallAudioReportDirection } from "./table-call-audio-reporting.js";
import type { TableCallParticipantDirectory } from "./table-call-participant-directory.js";
import type { MeetingClient } from "./table-call.context.js";
import type {
  AudioDiagnosticClassification,
  TableCallDiagnostics,
  TableCallRuntime,
  TableCallTarget,
} from "./table-call.types.js";

interface ClassifyArgs {
  error: unknown;
  direction: TableCallAudioReportDirection;
  operation: string;
  deviceKind?: "audioinput" | "audiooutput" | "none";
  preferredDeviceId?: string | null;
  currentDeviceId?: string | null;
  fallbackDeviceId?: string | null;
  rtkPermissionMessage?: string | null;
  meeting?: MeetingClient | null;
  target?: TableCallTarget | null;
  recoveryAttempt?: number;
}

export const useTableCallDiagnostics = (
  runtime: TableCallRuntime,
  participantDirectory: TableCallParticipantDirectory
): TableCallDiagnostics => {
  const classify = useCallback(
    ({
      error,
      direction,
      operation,
      deviceKind = "none",
      preferredDeviceId = null,
      currentDeviceId = null,
      fallbackDeviceId = null,
      rtkPermissionMessage = null,
      meeting = runtime.meetingRef.current,
      recoveryAttempt = 0,
    }: ClassifyArgs): AudioDiagnosticClassification => ({
      currentDeviceId:
        currentDeviceId ??
        (() => {
          if (!(meeting && deviceKind !== "none")) {
            return null;
          }
          try {
            const devices = meeting.self.getCurrentDevices();
            return deviceKind === "audioinput"
              ? (devices.audio?.deviceId ?? null)
              : (devices.speaker?.deviceId ?? null);
          } catch {
            return null;
          }
        })(),
      deviceKind,
      direction,
      fallbackDeviceId,
      issueKind: classifyAudioIssueKind(error, rtkPermissionMessage),
      operation,
      preferredDeviceId,
      recoverable: isRecoverableAudioError(error, rtkPermissionMessage),
      recoveryAttempt,
      rtkPermissionMessage,
    }),
    [runtime]
  );

  const report = useCallback(
    (
      error: unknown,
      {
        event,
        classification,
        extra,
      }: {
        event: string;
        classification: AudioDiagnosticClassification;
        extra?: Record<string, unknown>;
      }
    ) => {
      reportTableCallAudioError(error, {
        audioReadiness: runtime.audioReadinessRef.current,
        currentMeeting: runtime.meetingRef.current,
        direction: classification.direction,
        event,
        extra: {
          ...extra,
          audioClassification: {
            ...classification,
            reconnectReason: runtime.reconnectReasonRef.current,
          },
        },
        issueKind: classification.issueKind,
        meeting: runtime.meetingRef.current,
        operation: classification.operation,
        participantDirectory,
        preferredMicrophoneId: useDevicesStore.getState().preferredMicrophoneId,
        preferredSpeakerId: useDevicesStore.getState().preferredSpeakerId,
        status: runtime.statusRef.current,
        target: runtime.activeTargetRef.current
          ? {
              fluidId: runtime.activeTargetRef.current.fluidId,
              tableId: runtime.activeTargetRef.current.tableId,
            }
          : null,
      });
    },
    [participantDirectory, runtime]
  );

  const reportDeviceSyncRaceDropped = useCallback((reason: string) => {
    recordErrorInSentry(new Error("Device sync race dropped"), {
      extra: { reason },
      tags: { event: "device-sync-race-dropped", feature: "table-call" },
    });
  }, []);

  return useMemo(
    () => ({ classify, report, reportDeviceSyncRaceDropped }),
    [classify, report, reportDeviceSyncRaceDropped]
  );
};
